import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '~/lib/utils'

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-[10px]',
    'font-mono text-[12px] font-[500] tracking-[0.14em] uppercase',
    'rounded-none border cursor-pointer',
    'transition-[background,border-color,color] duration-[120ms] ease-in-out',
    'whitespace-nowrap select-none',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
    'disabled:pointer-events-none disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-accent border-accent text-accent-ink font-[600]',
          'hover:bg-accent-dark hover:border-accent-dark',
        ],
        ghost: [
          'bg-transparent border-[var(--line-strong)] text-ink',
          'hover:border-ink-soft',
        ],
        outline: [
          'bg-transparent border-[var(--line-strong)] text-ink',
          'hover:border-ink-soft hover:bg-surface',
        ],
      },
      size: {
        default: 'px-[22px] py-[14px]',
        lg: 'px-[28px] py-[18px] text-[13px]',
        sm: 'px-[14px] py-[10px] text-[10.5px] tracking-[0.12em]',
      },
    },
    defaultVariants: {
      variant: 'ghost',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
